import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getPrintOptions, setPrintOptions } from 'ducks/layout';
import { assemblyInstructionsLoaded } from 'ducks/assemblyInstructions';
import { useScreenshot } from 'contexts/screenshot';
import SheetBox from 'ui/Sheet/SheetBox';
import Sheet from 'ui/Sheet/Sheet';
import { KompisCheckboxGroup, KompisSheetHeader } from '@inter-ikea-kompis/react-components';
import Title from 'ui/Typography/Title';
import { useIntl } from 'react-intl';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import Body from 'ui/Typography/Body';
import Checkbox, { CheckboxLayoutEnum } from 'ui/Checkbox/Checkbox';
import { reportPrint } from 'analytics/reporters';
import { isPrintOptionsSheet, showPrintOptionsSheet, showPrintQuestionSheet } from 'ducks/sheets';
import { useAppDispatch } from 'ducks/hooks';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { rangeIsKnoxhult } from 'utils/queryUtils';

const Wrapper = styled(SheetBox)`
  height: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  padding: 1.5rem;
  border-top: 1px solid rgb(var(--colour-neutral-3, 223, 223, 223));
`;

const PrintWrapper = styled.div`
  @media print {
    visibility: hidden;
  }
`;

const PrintOptionsSheet = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const visible = useSelector(isPrintOptionsSheet);
  const printOptions = useSelector(getPrintOptions);

  const [hasConfirmed, setHasConfirmed] = useState(false);
  const {
    assemblyMainImageData,
    captureAssemblyInstructions,
    assemblyKitchenImageData,
    captureKitchenAssemblyInstructions
  } = useScreenshot();
  const assemblyLoaded = useSelector(assemblyInstructionsLoaded);
  const { assemblyOverview, productList, design } = printOptions;
  const [loading, setLoading] = useState<boolean>(false);
  const [assemblyOverviewCheck, setAssemblyOverviewCheck] = useState<boolean>(false);
  const captureInstructions = rangeIsKnoxhult()
    ? captureKitchenAssemblyInstructions
    : captureAssemblyInstructions;
  const assemblyInstructionsData = rangeIsKnoxhult()
    ? assemblyKitchenImageData
    : assemblyMainImageData;

  const onClose = () => {
    if (!loading) {
      dispatch(showPrintOptionsSheet(false));
    }
  };

  const goBack = () => {
    onClose();
    setTimeout(() => dispatch(showPrintQuestionSheet(true)), 10);
  };

  const onConfirm = () => {
    setLoading(true);
    reportPrint({
      currentPage: window.location.href,
      your_design: design,
      product_list: productList,
      assembly_overview: assemblyOverview,
      vpc: ''
    });
    setHasConfirmed(true);
    setAssemblyOverviewCheck(false);
  };

  onafterprint = () => {
    const element = document.getElementsByClassName('virtualKeyboard');
    if (element.length > 0) element[0]?.classList.remove('no-print');
  };

  useEffect(() => {
    if (visible) {
      dispatch(setPrintOptions({ ...printOptions, assemblyOverview: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!hasConfirmed) {
      return;
    }

    const print = () => {
      const element = document.getElementsByClassName('virtualKeyboard');
      if (element.length > 0) element[0]?.classList.add('no-print');

      setLoading(false);
      const printEvent = new Event('printsummary');
      window.dispatchEvent(printEvent);
      setHasConfirmed(false);
      dispatch(showPrintOptionsSheet(false));
    };

    if (assemblyOverview && assemblyLoaded) {
      print();
    } else if (!assemblyOverview && (design || productList)) {
      print();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConfirmed, assemblyLoaded]);

  return (
    <PrintWrapper>
      <Sheet onOverlayClick={onClose} data-testid="print-question-sheet" visible={visible}>
        {visible && (
          <>
            <KompisSheetHeader
              theme={SkapaTheme}
              backIcon={true}
              onCloseButtonClick={onClose}
              onBackButtonClick={goBack}
              heading=""
              zIndex={1}
            />
            <Wrapper>
              <Title type={ThemeFontStyleTypeEnum.headingMedium}>
                {formatMessage({ id: 'storageone.printOptionsTitle' })}
              </Title>
              <Body mt="xs" mb="s" type={ThemeFontStyleTypeEnum.bodyMedium}>
                {formatMessage({ id: 'storageone.printOptionsBody' })}
              </Body>
              <KompisCheckboxGroup theme={SkapaTheme}>
                <Checkbox
                  id="design"
                  onChange={event => {
                    dispatch(setPrintOptions({ ...printOptions, design: event.detail.checked }));
                  }}
                  label={formatMessage({ id: 'storageone.printOptionsDesign' })}
                  checked={design}
                  data-testid="option-design"
                  layout={CheckboxLayoutEnum.subtle}
                />
                <Checkbox
                  id="design"
                  onChange={event => {
                    dispatch(
                      setPrintOptions({ ...printOptions, productList: event.detail.checked })
                    );
                  }}
                  label={formatMessage({ id: 'storageone.printOptionsProductList' })}
                  checked={productList}
                  data-testid="option-product-list"
                  layout={CheckboxLayoutEnum.subtle}
                />
                {printOptions.showAssemblyOverview && (
                  <Checkbox
                    id="design"
                    onChange={event => {
                      if (assemblyInstructionsData.length === 0) {
                        setTimeout(() => {
                          captureInstructions();
                        }, 200);
                      }
                      dispatch(
                        setPrintOptions({ ...printOptions, assemblyOverview: event.detail.checked })
                      );
                      setAssemblyOverviewCheck(event.detail.checked);
                    }}
                    label={formatMessage({ id: 'storageone.printOptionsAssemblyOverview' })}
                    checked={assemblyOverviewCheck}
                    data-testid="option-assembly-overview"
                    layout={CheckboxLayoutEnum.subtle}
                  />
                )}
              </KompisCheckboxGroup>
            </Wrapper>
            <ButtonWrapper>
              <Button
                type={ButtonTypeEnum.primary}
                text={formatMessage({ id: 'storageone.print' })}
                onClick={onConfirm}
                width={'100%'}
                loading={loading}
                disabled={
                  !printOptions.design &&
                  !printOptions.productList &&
                  !printOptions.assemblyOverview
                }
              />
              <Button
                mt="md"
                type={ButtonTypeEnum.secondary}
                text={formatMessage({ id: 'kompis.buttonCancel' })}
                onClick={onClose}
                width={'100%'}
                disabled={loading}
              />
            </ButtonWrapper>
          </>
        )}
      </Sheet>
    </PrintWrapper>
  );
};

export default PrintOptionsSheet;
